import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { tabs } from '@/realtime/tabChannel';

/**
 * Device preferences (data-model.md B1): kept in this browser's localStorage
 * under `kindra.prefs`, kept after sign-out because they are not personal.
 * `system` follows the device's light/dark setting (spec US5 scenario 2).
 */
export type ThemePreference = 'system' | 'light' | 'dark';
const THEMES: readonly ThemePreference[] = ['system', 'light', 'dark'];

interface PreferencesState {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
}

function applyTheme(theme: ThemePreference): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = theme;
  }
}

/** localStorage that never throws (private mode, blocked storage). */
const safeStorage = createJSONStorage(() => ({
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // not persisting is acceptable; the choice still applies to this visit
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
}));

function setThemeLocally(theme: ThemePreference): boolean {
  if (!THEMES.includes(theme)) {
    return false;
  }
  usePreferencesStore.setState({ theme });
  applyTheme(theme);
  return true;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    () => ({
      theme: 'system' as ThemePreference,
      setTheme: (theme: ThemePreference) => {
        if (setThemeLocally(theme)) {
          tabs.post({ type: 'prefs:theme', theme });
        }
      },
    }),
    {
      name: 'kindra.prefs',
      storage: safeStorage,
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(THEMES.includes(state.theme) ? state.theme : 'system');
      },
    },
  ),
);

/** Applies the stored theme and follows theme changes made in other tabs. */
export function startPreferencesSync(): () => void {
  applyTheme(usePreferencesStore.getState().theme);
  return tabs.subscribe((message) => {
    if (message.type === 'prefs:theme') {
      setThemeLocally(message.theme);
    }
  });
}
